import React, { useState, useEffect, memo } from 'react'
import styled from 'styled-components'
import { logger, validateAxiosResponse } from '@dmi-mch/utils'
import classNames from 'classnames'

import { mq, px2rem, hiDPIpx, formatArtistLink } from '@utils'
import { remapArtworkWithSponsorGalleries } from '@utils/catalog'
import { Artworks as ArtworksService } from '@services'
import galleryStatus from '@constants/galleryStatus'
import { CustomLinkTag } from '@components'
import ArtworkItem from '../Artworks/ArtworkItem'
import tokens from '@mch-group/design-tokens/build/js/design_tokens-module'
import tokensRenamed from '@constants/tokensRenamed'
import { Container, Row, Col, Spinner, Icon } from '@mch-group/uikit-components'

interface IArtworksArtist {
  className?: string;
  artistId: number;
  id: number;
  linkProfile: string;
  artistCleanTitle: string;
  moreFromLabel: string;
  artistName: string;
  onArtworksLoaded?: (count: number) => void;
}

const mID = 'artworks-artist'

const allGalleryStatuses = [galleryStatus.live, galleryStatus.alumni, galleryStatus.outOfBusiness]

const ArtworksArtist: React.FC<IArtworksArtist> = memo(({
  className,
  artistId,
  id,
  linkProfile,
  artistCleanTitle,
  moreFromLabel,
  artistName,
  onArtworksLoaded
}) => {
  const [loading, setLoading] = useState(true)
  const [artworks, setArtworks] = useState<any[]>([])

  useEffect(() => {
    let isUnmounted = false
    async function fetchData() {
      try {
        const params = {
          limit: 5,
          galleryStatuses: allGalleryStatuses
        }
        const response = await ArtworksService.getArtworks({ artistId, ...params })

        if (validateAxiosResponse(response) && !isUnmounted) {
          //@ts-ignore
          const { items } = response.data
          const newItems = remapArtworkWithSponsorGalleries(items)
          newItems.forEach(i => delete i.sponsorGalleries)
          const filteredItems = newItems.filter(item => item.id !== id).slice(0, 4)
          setArtworks(filteredItems)
          setLoading(false)
        }
      } catch (e) {
        logger(e)
      }
      if (!isUnmounted) {
        setLoading(false)
      }
    }
    fetchData()

    return () => {
      isUnmounted = true
    }
  }, [artistId, id])

  useEffect(() => {
    if (!loading && onArtworksLoaded) onArtworksLoaded(artworks.length)
  }, [loading, artworks, onArtworksLoaded])

  if (!loading && artworks.length === 0) {
    return null
  }

  return (
    <Container className={classNames(mID, className, 'px-5 px-lg-8 px-md-7')}>
      <Row>
        <Col lg={9}>
          <h2>{`${moreFromLabel} ${artistName}`}</h2>
        </Col>
        <Col lg={3} className={`${mID}__link mobile hidden tablet hidden`}>
          <CustomLinkTag
            url={formatArtistLink(artistId, artistCleanTitle)}
            extraParams={{ id: artistId.toString(), cleanTitle: artistCleanTitle }}
          >
            <span>
              {linkProfile}
              <Icon name='chevron-right' />
            </span>
          </CustomLinkTag>
        </Col>
      </Row>

      <Row className={`${mID}__artworks`}>
        {artworks &&
          artworks.map(item => (
            <Col key={item.id} md={6} lg={3}>
              <div className='pb-5 pb-lg-0 pb-xl-3'>
                <ArtworkItem {...item} imageType='masonry' pageType='catalogue' isCatalougeArtwork />
              </div>
            </Col>
          ))}
      </Row>

      <Row className={`${mID}__link_mobile`}>
        <Col className={`${mID}__link_mobile__link`}>
          <CustomLinkTag
            url={formatArtistLink(artistId, artistCleanTitle)}
            extraParams={{ id: artistId.toString(), cleanTitle: artistCleanTitle }}
          >
            <span className='mb-4'>
              {linkProfile}
              <span className={`${mID}__link_mobile__caret`}>
                <Icon name='chevron-right' />
              </span>
            </span>
          </CustomLinkTag>
        </Col>
      </Row>
      {loading && <Spinner variant='button' theme='light' />}
    </Container>
  )
})

ArtworksArtist.displayName = 'ArtworksArtist'


export default styled(ArtworksArtist)`
  &&& {
    .${mID} {
      &__link_mobile {
        ${mq.greaterThan('largestTablet')`
          display: none;
        `}

        border-top: 1px solid ${tokens.color.light.base.neutrals[400].value};
        border-bottom: 1px solid ${tokens.color.light.base.neutrals[400].value};

        &__link {
          font-weight: ${tokens.fontWeights.artBaselHeadline0.value};
          font-size: ${tokens.fontSize[1].value};
          line-height: ${tokensRenamed.lineHeights[6].value};
        }

        &__caret {
          position: absolute;
          right: 0;
          ${mq.between('tablet', 'largestTablet')`
            width: ${hiDPIpx(16)};
          `}
          ${mq.lessThan('largestMobile')`
            width: ${px2rem(12)};
          `}
        }
      }

      &__link {
        text-align: right;
        font-size: ${tokens.fontSize[1].value};
        font-weight: ${tokens.fontWeights.artBaselHeadline0.value};
        line-height: ${tokensRenamed.lineHeights[6].value};
      }

      &__caret {
        margin-left: ${px2rem(20)};
      }

      &__artworks {
        margin-top: ${px2rem(20)};
        margin-bottom: ${px2rem(20)};
      }
    }
  }
`
